import { $t } from '@/i18n';
import { computed } from '@vue/composition-api';
import dependencyLoader from '@utils/dependencyLoader';
import { useStore } from '@/store';
import uploader from '@utils/upload';
import { message } from '@utils/wuji-message';
import hashString, { getRandomString } from '@utils/hashString';
import { isEqual } from 'lodash';
export const buildAndUploadDependency = async (newDependencies, projectInfo, newPacakgeJson) => {
    const { bundle, packageJson, presets, } = await dependencyLoader.update(Array.from(newDependencies), newPacakgeJson);
    const hash = await hashString(packageJson, 10);
    const bundlePath = {
        js: '',
        css: '',
    };
    const uploadFile = async (file, filename, type) => {
        const host = encodeURIComponent(location.hostname).replaceAll('.', '_');
        const prefix = `wuji_project_deps/${host}/${projectInfo.id}`;
        try {
            return await uploader.newUploadFile({
                file,
                pathPrefix: prefix,
                filename,
                projectId: projectInfo.id,
            });
        }
        catch (e) {
            if (e.message.includes('Overwriting existing files is not allowed')) {
                const prefix = projectInfo.dependency.bundle?.js?.split('/').slice(0, -1)
                    .join('/');
                if (prefix) {
                    return `${prefix}/bundle-${hash}.${type}`;
                }
                // 走到这里，大概率是因为用户把所有的依赖删了，导致bundle.js里的js是空
                // 然后又往回加入依赖，所以出现了既没有可以参考的前缀，文件名又重复的情况
                // 我们给文件名插入一个随机前缀来解决这个问题
                return uploadFile(file, `${getRandomString(4)}-${filename}`, type);
            }
        }
    };
    bundlePath.js = await uploadFile(new File([bundle.js], `bundle-${hash}.js`), `bundle-${hash}.js`, 'js');
    bundlePath.css = bundle.css ? await uploadFile(new File([bundle.css], `bundle-${hash}.css`), `bundle-${hash}.css`, 'css') : '';
    return {
        bundle: bundlePath,
        packageJson,
        presets,
    };
};
const useDepLoader = () => {
    const store = useStore();
    const projectInfo = computed(() => {
        if (window.RUNTIME_CONFIGS) {
            return store.state.runtime.project;
        }
        return store.state.project.projectInfo;
    });
    const currentBranch = computed(() => store.state.branch.currentBranch?.branch);
    const loadDependencies = (dependencies) => {
        const { packageJson, bundle, presets } = projectInfo.value.dependency || { packageJson: '', bundle: { js: '' }, presets: [] };
        if (!dependencies) {
            return;
        }
        return dependencyLoader
            .init({
            dependencies: Array.from(dependencies),
            packageJson,
            bundle,
            presets,
        })
            .catch((e) => {
            console.error($t('k_2f890239'), e);
        });
    };
    const updateDependency = async (dependencies, pageId) => {
        await store.dispatch('fetchProjectInfo', { projectId: projectInfo.value.id, branchId: currentBranch.value });
        // 由于fetchProjectInfo里更新基准baseInfo的操作有个setTimeout，所以这里也得setTimeout，否则还是会被当作有冲突
        await new Promise((resolve) => {
            setTimeout(() => {
                resolve(true);
            }, 0);
        });
        if (!dependencies)
            return;
        if (!projectInfo.value.dependency) {
            projectInfo.value.dependency = {
                pages: {},
            };
        }
        const gatherDepsFromAllPages = (pages) => {
            const deps = new Set();
            Object.entries(pages).forEach(([_key, pageDeps]) => {
                pageDeps.forEach((dep) => {
                    deps.add(dep);
                });
            });
            return Array.from(deps).sort();
        };
        const oldPageDeps = projectInfo.value.dependency?.pages || {};
        const oldDependencies = gatherDepsFromAllPages(oldPageDeps);
        const newDependencies = gatherDepsFromAllPages({
            ...oldPageDeps,
            [pageId]: Array.from(dependencies),
        });
        if (isEqual(oldDependencies, newDependencies)) {
            // 有可能总的依赖没变，但是当前这个页面的依赖变了
            // 这个场景我们也要更新项目信息，把这个页面的新依赖更新上去
            if (isEqual(oldPageDeps[pageId]?.sort(), Array.from(dependencies).sort())) {
                return;
            }
            projectInfo.value.dependency.pages = {
                ...projectInfo.value.dependency?.pages || {},
                [pageId]: Array.from(dependencies),
            };
            try {
                await store.dispatch('updateProjectInfo', { muted: true, id: projectInfo.value.id, dependency: projectInfo.value.dependency });
            }
            catch (e) {
                message.error($t('k_837e1a2b', { arg_0: e.message }));
                throw e;
            }
            return;
        }
        message.loading({ content: $t('k_e394ca14'), duration: 0 });
        try {
            const { bundle, packageJson, presets } = await buildAndUploadDependency(newDependencies, projectInfo.value);
            message.closeAll();
            projectInfo.value.dependency = {
                bundle,
                packageJson,
                presets,
                pages: {
                    ...oldPageDeps,
                    [pageId]: Array.from(dependencies),
                },
            };
            await store.dispatch('updateProjectInfo', { muted: true, id: projectInfo.value.id, dependency: projectInfo.value.dependency });
        }
        catch (e) {
            message.closeAll();
            message.error($t('k_c8f16779'));
            console.error(`updateDependency fail ${e?.message} ${e?.stack}`, e);
            throw e;
        }
    };
    return {
        loadDependencies,
        updateDependency,
    };
};
export default useDepLoader;
